/** The guest app's MUI theme and its two styled controls (`StyledTextField`,
 *  `StyledButton`), rebuilt from `GUEST`. Only the preview frame uses them, under its own
 *  nested `ThemeProvider` (a theme object replaces the console's outright). Roboto is
 *  self-hosted (`@fontsource/roboto`, the weights the guest app loads: 400 and 700) — no
 *  font CDN. */
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/700.css';
import { Button, createTheme, styled, TextField } from '@mui/material';

import { GUEST } from './guestTokens';

export const guestTheme = createTheme({
  palette: {
    primary: { main: GUEST.primary },
    secondary: { main: GUEST.secondary },
    background: { default: GUEST.pageBg },
    success: { main: GUEST.successMain, light: GUEST.successLight },
  },
  shape: { borderRadius: GUEST.radius },
  typography: { fontFamily: GUEST.font },
  components: {
    MuiCheckbox: { styleOverrides: { root: { color: GUEST.checkbox } } },
  },
});

/** bookingmgmt `components/StyledTextField.js`. */
export const GuestTextField = styled(TextField)({
  margin: 0,
  backgroundColor: '#FFFFFF',
  '& .MuiInputLabel-root': {
    fontSize: '16px',
    color: GUEST.label,
    transform: 'translate(17px, 13px) scale(1)',
    transition: 'transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
    '&.Mui-focused': { transform: 'translate(17px, 4px) scale(1)', color: GUEST.label },
    '&.MuiInputLabel-shrink': { fontSize: '13px', transform: 'translate(17px, 4px) scale(1)' },
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    padding: '8px 17px 8px',
    '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: GUEST.inputBorder, borderRadius: `${GUEST.radius}px` },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: GUEST.inputBorder },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GUEST.inputBorder, borderWidth: '1px' },
  },
  '& .MuiOutlinedInput-input': {
    fontSize: '16px',
    padding: '12px 0 0 0',
    height: '28px',
    color: '#000000',
    '&::placeholder': { opacity: 1, color: GUEST.placeholder },
  },
  '& .MuiInputBase-inputMultiline': { padding: '16px 0 0 0' },
});

/** bookingmgmt `components/StyledButton.js`. */
export const GuestButton = styled(Button)({
  textTransform: 'uppercase',
  letterSpacing: '0.0714em',
  fontWeight: 500,
  fontSize: '14px',
  padding: '6px 14px',
  borderRadius: `${GUEST.buttonRadius}px`,
  '&:hover': { backgroundColor: GUEST.buttonHover },
  '&.MuiButton-outlined:hover': { backgroundColor: GUEST.outlinedHover },
});

/** DynamicFields.js: the red outline of a required field left empty. */
export const REQUIRED_OUTLINE_SX = {
  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { borderColor: GUEST.error },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: GUEST.error },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GUEST.error },
} as const;

/** DynamicFields.js: the "* Required" caption under a field. */
export const REQUIRED_CAPTION_SX = { color: GUEST.error, fontSize: '12px', mt: 0.5, pl: 2, display: 'block' } as const;
